import { Link } from 'react-router-dom';
import { Building2, Shield, Crown, Hammer, Lock, TrendingUp, ArrowRight } from 'lucide-react';
import SEO from '../components/shared/SEO';

const CoreValuesPage = () => {
  const pillars = [
    {
      icon: Building2,
      title: 'Build',
      tagline: 'Foundation First',
      color: '#CD7F32',
      description: 'Every successful carrier operation starts with a solid foundation. We believe in doing things right from day one.',
      values: [
        {
          title: 'Structure',
          description: 'A proper business structure protects you legally and positions you for growth. No shortcuts.'
        },
        {
          title: 'Preparation',
          description: 'Authority activation is just the beginning. True readiness means having systems, knowledge, and resources in place.'
        },
        {
          title: 'Investment',
          description: 'Building right costs more upfront but saves tens of thousands in mistakes, fines, and wasted time.'
        }
      ],
      link: '/build'
    },
    {
      icon: Shield,
      title: 'Protect',
      tagline: 'Compliance Without Compromise',
      color: '#C0C0C0',
      description: 'Your authority is your livelihood. Protecting it means staying ahead of deadlines, not reacting to problems.',
      values: [
        {
          title: 'Vigilance',
          description: 'Compliance isn\'t a one-time task—it\'s ongoing monitoring, tracking, and proactive management.'
        },
        {
          title: 'Accountability',
          description: 'We hold ourselves and our clients to high standards. Your compliance status is always visible, never hidden.'
        },
        {
          title: 'Prevention',
          description: 'The cost of a violation far exceeds the cost of prevention. We believe in staying ahead, not catching up.'
        }
      ],
      link: '/protect'
    },
    {
      icon: Crown,
      title: 'Grow',
      tagline: 'Revenue With Purpose',
      color: '#D4AF37',
      description: 'Growth isn\'t just about more loads—it\'s about better rates, smarter lanes, and sustainable profitability.',
      values: [
        {
          title: 'Strategy',
          description: 'Every load decision should serve your long-term goals. We don\'t chase volume—we build revenue.'
        },
        {
          title: 'Partnership',
          description: 'Dispatch is a relationship, not a transaction. Your success is our success, and we operate accordingly.'
        },
        {
          title: 'Excellence',
          description: 'Premium rates come from premium service. We maintain high standards because the market rewards quality.'
        }
      ],
      link: '/grow'
    }
  ];

  return (
    <div data-testid="core-values-page" className="bg-[#0A0A0A]">
      <SEO 
        title="Our Core Values | Queen B Logistics"
        description="Our values are built around three pillars: Build with structure, Protect with vigilance, Grow with strategy. Discover what drives Queen B Logistics."
        canonical="https://queenblogistics.com/core-values"
        keywords="Queen B Logistics values, dispatch company principles, trucking company values, Build Protect Grow"
      />
      
      {/* Page Header */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-8">
            <span className="text-[#D4AF37] text-sm font-medium tracking-wider uppercase">
              What We Stand For
            </span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase leading-none mb-6">
            Core <span className="text-[#D4AF37]">Values</span>
          </h1>
          <p className="text-white/60 text-lg max-w-2xl leading-relaxed">
            Our values aren't abstract concepts—they're the principles that guide every decision, 
            every interaction, and every service we provide. They're organized around the same 
            framework that structures our entire operation.
          </p>
        </div>
      </section>

      {/* Build Protect Grow Framework */}
      <section className="py-8 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center gap-4 md:gap-8">
            <span className="text-[#CD7F32] font-['Barlow_Condensed'] text-xl md:text-2xl font-bold uppercase">Build</span>
            <span className="text-white/20">•</span>
            <span className="text-[#C0C0C0] font-['Barlow_Condensed'] text-xl md:text-2xl font-bold uppercase">Protect</span>
            <span className="text-white/20">•</span>
            <span className="text-[#D4AF37] font-['Barlow_Condensed'] text-xl md:text-2xl font-bold uppercase">Grow</span>
          </div>
        </div>
      </section>

      {/* Values by Pillar */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-20 md:space-y-32">
            {pillars.map((pillar, index) => (
              <div 
                key={pillar.title}
                className="relative"
                data-testid={`pillar-${pillar.title.toLowerCase()}`}
              >
                {/* Pillar Header */}
                <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-8 mb-12">
                  <div 
                    className="w-16 h-16 flex items-center justify-center"
                    style={{ backgroundColor: `${pillar.color}15`, border: `1px solid ${pillar.color}40` }}
                  >
                    <pillar.icon className="w-8 h-8" style={{ color: pillar.color }} />
                  </div>
                  <div>
                    <span 
                      className="text-sm font-bold uppercase tracking-widest"
                      style={{ color: pillar.color }}
                    >
                      {pillar.tagline}
                    </span>
                    <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mt-1">
                      {pillar.title}
                    </h2>
                  </div>
                </div>

                {/* Pillar Description */}
                <p className="text-white/60 text-lg leading-relaxed mb-12 max-w-3xl">
                  {pillar.description}
                </p>

                {/* Values Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                  {pillar.values.map((value, vIndex) => (
                    <div 
                      key={value.title}
                      className="bg-[#121212] border border-white/10 p-6 md:p-8 relative overflow-hidden hover:border-white/20 transition-colors"
                    >
                      {/* Accent Line */}
                      <div 
                        className="absolute top-0 left-0 w-full h-1"
                        style={{ backgroundColor: pillar.color }}
                      />
                      
                      {/* Value Number */}
                      <span 
                        className="font-['Barlow_Condensed'] text-5xl font-black opacity-10 absolute top-4 right-4"
                        style={{ color: pillar.color }}
                      >
                        {String(vIndex + 1).padStart(2, '0')}
                      </span>
                      
                      {/* Value Content */}
                      <h3 
                        className="font-['Barlow_Condensed'] text-2xl font-bold uppercase mb-4"
                        style={{ color: pillar.color }}
                      >
                        {value.title}
                      </h3>
                      <p className="text-white/60 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Pillar Link */}
                <div className="mt-8">
                  <Link
                    to={pillar.link}
                    className="inline-flex items-center gap-2 font-medium transition-colors"
                    style={{ color: pillar.color }}
                  >
                    Learn about {pillar.title} services
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Divider */}
                {index < pillars.length - 1 && (
                  <div className="absolute -bottom-10 md:-bottom-16 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Unifying Principle */}
      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase mb-6">
            The Common Thread
          </h3>
          <p className="text-white/60 text-lg leading-relaxed mb-8">
            Whether we're helping you build your authority, protect your compliance, or grow your revenue, 
            the same principles apply: <span className="text-white">do it right, do it proactively, and do it with purpose.</span>
          </p>
          <div className="inline-flex items-center gap-4 px-6 py-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <span className="text-[#D4AF37] font-['Barlow_Condensed'] text-xl font-bold uppercase">
              Infrastructure, Not Guesswork
            </span>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-6">
            Ready to Work <span className="text-[#D4AF37]">With Us?</span>
          </h2>
          <p className="text-white/50 text-lg mb-10 max-w-2xl mx-auto">
            If these values resonate with how you want to run your operation, we should talk.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/intake"
              className="w-full sm:w-auto px-10 py-5 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors flex items-center justify-center gap-2"
            >
              Apply Now
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/inside-the-hive"
              className="w-full sm:w-auto px-10 py-5 border border-white/20 text-white font-bold uppercase tracking-wider hover:bg-white/5 transition-colors"
            >
              Inside The Hive
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CoreValuesPage;
